import React from 'react';
import styled from '@emotion/styled';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { useSelector, useDispatch } from 'react-redux';
import { useMenu } from '../../hooks/useMenu';
import { Drawer as MuiDrawer, Grid, IconButton, List as MuiList, ListItem, ListItemButton, ListItemIcon, ListItemText, Collapse } from '@mui/material';
import * as Icons from '@mui/icons-material'
import img from '../../assets/images/Logo_AIAutomation_Blanco.png';
import { ChevronLeft, ExpandMore, ExpandLess, Menu as MenuIcon, } from '@mui/icons-material';
import { Box } from '@mui/system';
import { DialogConfirmAction } from '../dialogs/DialogConfirmAction';
import { changesPendingFlows } from '../../redux/actions/flows';
import { useNotification } from '../../hooks/useNotification';

const drawerWidth = 260;

const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    background: `linear-gradient( ${theme.palette.navigationBar.main}, ${theme.palette.navigationBar.secondary} )`,
    overflowX: 'hidden'
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    background: `linear-gradient( ${theme.palette.navigationBar.main}, ${theme.palette.navigationBar.secondary} )`,
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(10)} + 1px)`,
    }
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }),
);

const List = styled(MuiList)(({ theme }) => ({
    '& .MuiListItem-root': {
        '& .MuiListItemIcon-root': {
            color: theme.palette.navigationBar.text
        },
        '& .MuiListItemText-root span': {
            color: theme.palette.navigationBar.text,
            fontWeight: 'bold'
        },
    },
    '& .MuiListItem-root.Mui-selected': {
        backgroundColor: theme.palette.navigationBar.selected,
        borderRadius: '8px 8px 8px 8px',
        boxShadow: '0px 0px 9px 4px rgba(222,222,222,1)',
        '& .MuiListItemIcon-root': {
            color: theme.palette.primary.main
        },
        '& .MuiListItemText-root span': {
            color: theme.palette.primary.main,
            fontWeight: 'bold'
        },
    },
    '& .MuiListItemButton-root': {
        color: theme.palette.navigationBar.text,
    },
    '& .MuiListItemButton-root:hover': {
        backgroundColor: theme.palette.navigationBar.hover,
        color: theme.palette.primary.main,
        borderRadius: '8px 8px 8px 8px',
        boxShadow: 'px 0px 9px 1px rgba(0,0,0,0.75)',
        '& .MuiListItemIcon-root': {
            color: theme.palette.primary.main,
        },
        '& .MuiListItemText-root span': {
            color: theme.palette.primary.main,
            fontWeight: 'bold'
        },
    },
}));

export const Sidebar = ({ item, openDrawer, handleDrawerOpen, handleDrawerClose }) => {
    const { isMobileScreen } = usePWAInstall();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [sendNotification] = useNotification();
    const [activeOption, setActiveOption] = React.useState(location.pathname);
    const [openDialogConfirmNotSaveChanges, setOpenDialogConfirmNotSaveChanges] = React.useState(false);
    const [openMenu, setOpenMenu] = React.useState(null);
    const [itemMenu, setitemMenu] = React.useState(null);
    const { isChangesPendingFlow } = useSelector(state => state.flows)
    const { actionRequired, changesPending } = useSelector(state => state.sessionState)
    const [{ menu }] = useMenu();

    const validateChangesPending = (itemM) => {
        setitemMenu(itemM);
        if (actionRequired) {
            sendNotification({
                msg: "Debes completar esta acción para continuar",
                variant: "error",
            });
        }
        else if (isChangesPendingFlow || changesPending)
            setOpenDialogConfirmNotSaveChanges(true);
        else
            handleClick(itemM)
    }

    const handleConfirmeNotSaveChanges = () => {
        setOpenDialogConfirmNotSaveChanges(false);
        handleClick(itemMenu);
        dispatch(changesPendingFlows(false));
    }

    const handleToggle = (name) => setOpenMenu((prev) => (prev === name ? null : name));

    const handleClick = (item) => {
        setActiveOption(item.name);
        navigate(item.path, { state: { configuration: item.configuration, title: item.name, config: item } });
        if (isMobileScreen) {
            handleDrawerClose();
        }
    };

    const drawerContent = (
        <>
            <Box>
                {openDrawer ? (
                    <Grid container sx={{ pt: 1, pb: 0.5 }}>
                        <Grid size={{ xs: 8 }}
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <img src={img} alt="imagen" style={{ height: '70px', }} />
                        </Grid>
                        <Grid size={{ xs: 4 }}
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="flex-start"
                        >
                            <IconButton onClick={handleDrawerClose}>
                                <ChevronLeft color="primary" fontSize="large" />
                            </IconButton>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid
                        size={{ xs: 12 }}
                        sx={{ marginLeft: '20px' }}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            sx={{ marginRight: 2 }}
                            onClick={handleDrawerOpen}
                        >
                            <MenuIcon color="primary" fontSize="large" />
                        </IconButton>
                    </Grid>
                )}
            </Box>

            <Box sx={{ overflowY: 'auto', overflowX: 'hidden', flexGrow: 1 }}>
                <List sx={{ mx: 0.7 }}>
                    {menu.map((item) => (
                        <React.Fragment key={item.name}>
                            {item.type === 'unique' ? (
                                <ListItem
                                    key={`unique-${item.name}`}
                                    onClick={() => validateChangesPending(item)}

                                    disablePadding
                                    sx={{ my: 0.5 }}
                                >
                                    <ListItemButton
                                        className='sidebarMenuItemButton'
                                        sx={{
                                            justifyContent: openDrawer ? 'initial' : 'center',
                                            pl: 2.5,
                                            backgroundColor: activeOption === item.name ? '#6728AF' : 'transparent',
                                        }}
                                    >
                                        <ListItemIcon
                                            className={openDrawer ? 'mr14' : 'mr0'}
                                            sx={{
                                                minWidth: 0,
                                                mr: openDrawer ? 2 : 'auto',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {React.createElement(Icons[item.icon] || Icons.MenuOutlined)}
                                        </ListItemIcon>
                                        <ListItemText
                                            sx={{
                                                opacity: openDrawer ? 1 : 0,
                                                display: 'block',
                                                textDecoration: 'none',
                                                color: (theme) => theme.palette.text.main,
                                                fontFamily: (theme) => theme.palette.customTableZDNix.tableHeaderFontFamily,
                                            }}
                                        >
                                            {item.name}
                                        </ListItemText>
                                    </ListItemButton>
                                </ListItem>
                            ) : (
                                <>
                                    <ListItem
                                        disablePadding
                                        sx={{ my: 0.5 }}
                                    >
                                        <ListItemButton
                                            sx={{
                                                justifyContent: openDrawer ? 'initial' : 'center',
                                                pl: 2.5,
                                                backgroundColor:
                                                    activeOption === item.name ? '#6728AF' : 'transparent',
                                            }}
                                            onClick={() => handleToggle(item.menu)}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    minWidth: 0,
                                                    mr: openDrawer ? 3 : 'auto',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {React.createElement(Icons[item.mainIcon] || Icons.MenuOutlined)}
                                            </ListItemIcon>

                                            <ListItemText
                                                sx={{
                                                    opacity: openDrawer ? 1 : 0,
                                                    display: 'block',
                                                    textDecoration: 'none',
                                                    color: (theme) => theme.palette.text.main,
                                                    fontFamily: (theme) => theme.palette.customTableZDNix.tableHeaderFontFamily
                                                }}
                                            >
                                                {item.menu}
                                            </ListItemText>
                                            <IconButton
                                                edge="end"
                                                size="small"
                                            >
                                                {openMenu === item.menu ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />}
                                            </IconButton>
                                        </ListItemButton>
                                    </ListItem>
                                    <Collapse in={openMenu === item.menu} timeout="auto" unmountOnExit>
                                        <List component="div">
                                            {item.body.map((subItem, subIndex) => (
                                                <ListItem
                                                    key={subIndex}
                                                    onClick={() => validateChangesPending(subItem)}
                                                    disablePadding
                                                    sx={{ pl: 2 }}
                                                >
                                                    <ListItemButton
                                                        sx={{
                                                            justifyContent: openDrawer ? 'initial' : 'center',
                                                            pl: 2,
                                                            backgroundColor: activeOption === subItem.name ? '#6728AF' : 'transparent'
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={{
                                                                minWidth: 0,
                                                                mr: openDrawer ? 1.5 : 'auto',
                                                                justifyContent: 'center',
                                                            }}
                                                        >
                                                            {React.createElement(Icons[subItem.icon] || Icons.MenuOutlined)}
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            sx={{
                                                                opacity: openDrawer ? 1 : 0,
                                                                display: 'block',
                                                                textDecoration: 'none',
                                                                color: (theme) => theme.palette.text.main
                                                            }}
                                                        >
                                                            {subItem.name}
                                                        </ListItemText>
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Collapse>
                                </>
                            )}
                        </React.Fragment>
                    ))}
                </List>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
        </>
    );

    return (
        <React.Fragment>
            {isMobileScreen ? (
                // Drawer temporal para mobile (overlay)
                <MuiDrawer
                    variant="temporary"
                    open={openDrawer}
                    onClose={handleDrawerClose}
                    ModalProps={{
                        keepMounted: true, // Mejor rendimiento en mobile
                    }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            background: (theme) => `linear-gradient( ${theme.palette.navigationBar.main}, ${theme.palette.navigationBar.secondary} )`,
                        },
                    }}
                >
                    {drawerContent}
                </MuiDrawer>
            ) : (
                // Drawer permanente para desktop
                <Drawer
                    variant="permanent"
                    open={openDrawer}
                    sx={{
                        display: { xs: 'none', md: 'block' },
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}

            <DialogConfirmAction
                openDialogConfirm={openDialogConfirmNotSaveChanges}
                setOpenDialogConfirm={setOpenDialogConfirmNotSaveChanges}
                titleDialog={"Changes Pending To Save"}
                textConfirmAction={"You have unsaved changes, do you want to continue?"}
                textButtonConfirm={"Continue"}
                textButtonCancel={"Cancel"}
                handleConfirmAction={handleConfirmeNotSaveChanges}
            />
        </React.Fragment>
    )
}